import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaInstagram } from 'react-icons/fa';

import { usePublicExperience } from '../context/PublicExperienceContext';
import { getStoryFrameLoadingProfile } from '../utils/storyFrameLoading';
import HomeCarousel from './HomeCarousel';
import PublicActionLink from './publicExperience/PublicActionLink';
import './Home.css';

const HOME_HERO_DESKTOP_IMAGE = '/images/capa-desktop.png';
const HOME_HERO_MOBILE_IMAGE = '/images/capa.png';
const HERO_FADE_SCROLL_VIEWPORTS = 0.28;
const STORY_SCROLL_DISTANCE_VIEWPORTS = 1.05;
const STORY_FRAME_COUNT_DEFAULT = 240;
const STORY_FRAME_PREFIX_DEFAULT = '/videos/brownie-frames/frame-';
const STORY_FRAME_EXTENSION_DEFAULT = 'webp';
const STORY_FRAME_ANIMATION_DIVISOR = 5;
const STORY_FRAME_STEP_MAX = 4;
const STORY_FRAME_IDLE_FALLBACK_MS = 120;
const STORY_FRAME_PRELOAD_RESUME_WINDOW = 6;
const STORY_INTERACTION_SYNC_MS = 220;
const STORY_INTERACTION_MOMENTUM_MS = 340;
const STORY_POINTER_SCROLL_DELTA = 0.5;
const STORY_BUTTON_SCROLL_DESKTOP_MS = 2800;
const STORY_BUTTON_SCROLL_MOBILE_MS = 3200;
const isInstagramLink = (link) =>
  String(link?.id || '').toLowerCase().includes('instagram') ||
  String(link?.label || '').toLowerCase().includes('instagram') ||
  String(link?.href || '').toLowerCase().includes('instagram.com');
const extractInstagramHandle = (href) => {
  if (!href) {
    return '';
  }

  try {
    const { pathname } = new URL(href);
    const handle = pathname.replace(/\//g, '').trim();
    return handle ? `@${handle}` : '';
  } catch (error) {
    const normalizedHref = String(href).trim();
    return normalizedHref.startsWith('@') ? normalizedHref : '';
  }
};
const padFrameNumber = (value) => String(value).padStart(3, '0');

function Home() {
  const { experience } = usePublicExperience();
  const branding = experience?.data?.branding || {};
  const footerLinks = Array.isArray(experience?.data?.navigation?.footer)
    ? experience.data.navigation.footer
        .filter((link) => link?.visible !== false)
        .sort((left, right) => Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0))
    : [];
  const hero = experience?.data?.pages?.home?.hero || {};
  const story = experience?.data?.pages?.home?.story || {};
  const heroCta = hero?.cta || { label: 'Ver Card\u00e1pio', href: '/cardapio', kind: 'internal' };
  const heroPhrase =
    hero?.headline ||
    'Brownies artesanais, presentes delicados e um card\u00e1pio pensado para presentear bem.';
  const storyPrompt = 'Conhe\u00e7a nossa hist\u00f3ria';
  const storyEyebrow = story?.eyebrow || 'Nossa hist\u00f3ria';
  const storyTitle = story?.title || 'Uma receita feita para virar mem\u00f3ria';
  const storyTextSource =
    story?.text ||
    'A Glac\u00ea nasceu do desejo de transformar o doce do dia a dia em um gesto de afeto. Entre fornadas, testes e embalagens pensadas com carinho, fomos criando uma confeitaria que mistura sabor intenso, textura macia e um olhar delicado para presentear.\n\nHoje cada caixa sai da nossa cozinha com o mesmo cuidado do come\u00e7o: ingredientes selecionados, acabamento artesanal e a vontade de fazer cada pedido chegar com cara de momento especial.';
  const storyText = storyTextSource
    .replace(/\bBrownie\b/g, 'Doce')
    .replace(/\bbrownie\b/g, 'doce')
    .replace(/\bBrownies\b/g, 'Doces')
    .replace(/\bbrownies\b/g, 'doces');
  const storyFrameCount = Math.max(
    1,
    Number(
      story?.frameCount || story?.imageSequenceCount || story?.scrollFrameCount || STORY_FRAME_COUNT_DEFAULT,
    ),
  );
  const storyFramePrefix =
    story?.framePrefix || story?.imageSequencePrefix || story?.scrollFramePrefix || STORY_FRAME_PREFIX_DEFAULT;
  const storyFrameExtension =
    story?.frameExtension ||
    story?.imageSequenceExtension ||
    story?.scrollFrameExtension ||
    STORY_FRAME_EXTENSION_DEFAULT;
  const storyFrameSources = useMemo(
    () =>
      Array.from({ length: storyFrameCount }, (_, frameIndex) =>
        `${storyFramePrefix}${padFrameNumber(frameIndex + 1)}.${storyFrameExtension}`,
      ),
    [storyFrameCount, storyFrameExtension, storyFramePrefix],
  );
  const storyPosterSrc = story?.posterSrc || storyFrameSources[0];
  const instagramHref =
    footerLinks.find((link) => isInstagramLink(link))?.href || branding?.contact?.instagram || '';
  const instagramHandle = extractInstagramHandle(instagramHref);
  const slides = useMemo(
    () => [
      { src: '/images/midia.carrossel1.png', alt: 'Foto 1' },
      { src: '/images/midia.carrossel2.png', alt: 'Foto 2' },
      { src: '/images/midia.carrossel3.png', alt: 'Foto 3' },
    ],
    [],
  );
  const heroRef = useRef(null);
  const storySectionRef = useRef(null);
  const storyFrameImageRef = useRef(null);
  const storyFrameBufferRef = useRef(null);
  const storyFrameVisibleSlotRef = useRef(0);
  const storyFrameTargetIndexRef = useRef(0);
  const storyFrameCurrentIndexRef = useRef(0);
  const storyFrameAnimationFrameRef = useRef(0);
  const storyFrameSyncFrameRef = useRef(0);
  const storyButtonScrollFrameRef = useRef(0);
  const storyFrameCacheRef = useRef(new Map());
  const storyLoadedFrameIndexesRef = useRef(new Set());
  const storyPendingFrameIndexesRef = useRef(new Set());
  const storyTouchTrackingUntilRef = useRef(0);
  const storyLastTrackedScrollYRef = useRef(0);
  const [storyLoadingEnabled, setStoryLoadingEnabled] = useState(false);
  const cancelStoryButtonScroll = useCallback(() => {
    if (storyButtonScrollFrameRef.current) {
      window.cancelAnimationFrame(storyButtonScrollFrameRef.current);
      storyButtonScrollFrameRef.current = 0;
    }
  }, []);

  const scrollToStory = () => {
    const section = storySectionRef.current;
    if (!section) {
      return;
    }

    setStoryLoadingEnabled(true);
    cancelStoryButtonScroll();

    const start = window.scrollY;
    const scrollMargin = parseFloat(window.getComputedStyle(section).scrollMarginTop) || 0;
    const sectionTop = start + section.getBoundingClientRect().top;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const target = Math.max(0, Math.min(sectionTop - scrollMargin, maxScroll));
    const distance = target - start;

    if (Math.abs(distance) < 1) {
      return;
    }

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      window.scrollTo({ top: target, behavior: 'instant' });
      return;
    }

    const baseDuration = window.innerWidth <= 768
      ? STORY_BUTTON_SCROLL_MOBILE_MS
      : STORY_BUTTON_SCROLL_DESKTOP_MS;
    const duration = Math.max(
      600,
      baseDuration * Math.min(1, Math.abs(distance) / (window.innerHeight * 0.8)),
    );
    let startTime;

    const animateScroll = (timestamp) => {
      startTime ??= timestamp;
      const progress = Math.min(1, (timestamp - startTime) / duration);
      const easedProgress = progress - Math.sin(progress * Math.PI * 2) * 0.04;
      window.scrollTo({ top: start + distance * easedProgress, behavior: 'instant' });

      if (progress < 1) {
        storyButtonScrollFrameRef.current = window.requestAnimationFrame(animateScroll);
      } else {
        storyButtonScrollFrameRef.current = 0;
      }
    };

    storyButtonScrollFrameRef.current = window.requestAnimationFrame(animateScroll);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Escape'].includes(event.key)) {
        cancelStoryButtonScroll();
      }
    };

    window.addEventListener('wheel', cancelStoryButtonScroll, { passive: true });
    window.addEventListener('touchstart', cancelStoryButtonScroll, { passive: true });
    window.addEventListener('pointerdown', cancelStoryButtonScroll);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', cancelStoryButtonScroll);

    return () => {
      cancelStoryButtonScroll();
      window.removeEventListener('wheel', cancelStoryButtonScroll);
      window.removeEventListener('touchstart', cancelStoryButtonScroll);
      window.removeEventListener('pointerdown', cancelStoryButtonScroll);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', cancelStoryButtonScroll);
    };
  }, [cancelStoryButtonScroll]);

  useEffect(() => {
    const heroElement = heroRef.current;
    if (!heroElement) {
      return undefined;
    }

    let animationFrame = 0;
    let lastProgress = -1;
    const updateFade = () => {
      animationFrame = 0;
      const fadeDistance = Math.max(1, window.innerHeight * HERO_FADE_SCROLL_VIEWPORTS);
      const linearProgress = Math.min(1, Math.max(0, window.scrollY / fadeDistance));
      const progress = window.innerWidth <= 768 ? Math.sqrt(linearProgress) : linearProgress;
      if (progress !== lastProgress) {
        heroElement.style.setProperty('--hero-fade-progress', progress);
        lastProgress = progress;
      }
    };
    const requestFadeUpdate = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(updateFade);
      }
    };

    updateFade();
    window.addEventListener('scroll', requestFadeUpdate, { passive: true });
    window.addEventListener('resize', requestFadeUpdate);

    return () => {
      window.removeEventListener('scroll', requestFadeUpdate);
      window.removeEventListener('resize', requestFadeUpdate);
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  useEffect(() => {
    const section = storySectionRef.current;

    if (storyLoadingEnabled || !section) {
      return undefined;
    }

    if (typeof window.IntersectionObserver !== 'function') {
      setStoryLoadingEnabled(true);
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        setStoryLoadingEnabled(true);
        observer.disconnect();
      },
      { threshold: 0.01 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [storyLoadingEnabled]);

  useEffect(() => {
    const imageElement = storyFrameImageRef.current;
    const bufferElement = storyFrameBufferRef.current;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    storyFrameCacheRef.current = new Map();
    storyLoadedFrameIndexesRef.current = new Set();
    storyPendingFrameIndexesRef.current = new Set();
    storyFrameCurrentIndexRef.current = 0;
    storyFrameTargetIndexRef.current = 0;

    if (!imageElement || !bufferElement) {
      return undefined;
    }

    imageElement.src = storyPosterSrc;
    imageElement.dataset.frameIndex = '0';
    imageElement.style.visibility = 'visible';
    imageElement.removeAttribute('aria-hidden');
    bufferElement.style.visibility = 'hidden';
    bufferElement.setAttribute('aria-hidden', 'true');
    storyFrameVisibleSlotRef.current = 0;

    if (prefersReducedMotion || !storyLoadingEnabled) {
      return undefined;
    }

    let cancelled = false;
    let idleId = 0;
    let timeoutId = 0;
    const loadingProfile = getStoryFrameLoadingProfile(
      window.navigator?.connection,
      window.matchMedia?.('(max-width: 700px)')?.matches,
    );
    const queueFrameLoad = (frameIndex, priority = 'auto') => {
      if (
        cancelled ||
        frameIndex < 0 ||
        frameIndex >= storyFrameSources.length ||
        storyLoadedFrameIndexesRef.current.has(frameIndex) ||
        storyPendingFrameIndexesRef.current.has(frameIndex)
      ) {
        return;
      }

      const frameSrc = storyFrameSources[frameIndex];

      if (!frameSrc) {
        return;
      }

      const frameImage = new Image();
      let settled = false;
      const finalizeLoad = () => {
        if (cancelled || settled) {
          return;
        }

        settled = true;
        storyPendingFrameIndexesRef.current.delete(frameIndex);
        storyFrameCacheRef.current.delete(frameIndex);
        storyLoadedFrameIndexesRef.current.add(frameIndex);
      };
      const finalizeError = () => {
        if (cancelled || settled) {
          return;
        }

        settled = true;
        storyPendingFrameIndexesRef.current.delete(frameIndex);
        storyFrameCacheRef.current.delete(frameIndex);
      };

      frameImage.decoding = 'async';

      if ('fetchPriority' in frameImage && priority === 'high') {
        frameImage.fetchPriority = 'high';
      }

      storyPendingFrameIndexesRef.current.add(frameIndex);
      storyFrameCacheRef.current.set(frameIndex, frameImage);

      frameImage.onload = finalizeLoad;
      frameImage.onerror = finalizeError;
      frameImage.src = frameSrc;

      if (frameImage.complete && frameImage.naturalWidth > 0) {
        finalizeLoad();
      }
    };
    const initialFrameCount = Math.min(
      loadingProfile.initialFrameCount,
      storyFrameSources.length,
    );

    for (let frameIndex = 0; frameIndex < initialFrameCount; frameIndex += 1) {
      queueFrameLoad(
        frameIndex,
        frameIndex < loadingProfile.highPriorityFrameCount ? 'high' : 'auto',
      );
    }

    let nextFrameIndex = initialFrameCount;

    const preloadNextBatch = () => {
      idleId = 0;
      timeoutId = 0;

      if (cancelled || nextFrameIndex >= storyFrameSources.length) {
        return;
      }

      const batchEndIndex = Math.min(
        nextFrameIndex + loadingProfile.batchSize,
        storyFrameSources.length,
      );

      while (nextFrameIndex < batchEndIndex) {
        queueFrameLoad(nextFrameIndex);
        nextFrameIndex += 1;
      }

      if (nextFrameIndex < storyFrameSources.length) {
        timeoutId = window.setTimeout(
          scheduleNextBatch,
          loadingProfile.batchDelayMs,
        );
      }
    };

    const scheduleNextBatch = () => {
      timeoutId = 0;

      if (cancelled) {
        return;
      }

      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(preloadNextBatch, { timeout: 1000 });
        return;
      }

      timeoutId = window.setTimeout(preloadNextBatch, STORY_FRAME_IDLE_FALLBACK_MS);
    };

    if (loadingProfile.preloadRemaining && nextFrameIndex < storyFrameSources.length) {
      timeoutId = window.setTimeout(scheduleNextBatch, loadingProfile.batchDelayMs);
    }

    return () => {
      cancelled = true;
      storyFrameCacheRef.current = new Map();
      storyLoadedFrameIndexesRef.current = new Set();
      storyPendingFrameIndexesRef.current = new Set();

      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [storyFrameSources, storyLoadingEnabled, storyPosterSrc]);

  useEffect(() => {
    const section = storySectionRef.current;
    const imageElement = storyFrameImageRef.current;
    const bufferElement = storyFrameBufferRef.current;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (
      !storyLoadingEnabled ||
      !section ||
      !imageElement ||
      !bufferElement ||
      storyFrameSources.length === 0 ||
      prefersReducedMotion
    ) {
      return undefined;
    }

    const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
    const lastFrameIndex = storyFrameSources.length - 1;
    const loadingProfile = getStoryFrameLoadingProfile(
      window.navigator?.connection,
      window.matchMedia?.('(max-width: 700px)')?.matches,
    );
    const getScrollPosition = () =>
      window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    let cancelled = false;
    let pendingFrameIndex = -1;

    const renderFrame = (frameIndex) => {
      const clampedFrameIndex = clamp(frameIndex, 0, lastFrameIndex);
      const nextFrameSrc = storyFrameSources[clampedFrameIndex];

      if (!nextFrameSrc || pendingFrameIndex !== -1) {
        return;
      }

      if (storyFrameCurrentIndexRef.current === clampedFrameIndex) {
        return;
      }

      const visibleElement = storyFrameVisibleSlotRef.current === 0 ? imageElement : bufferElement;
      const nextElement = storyFrameVisibleSlotRef.current === 0 ? bufferElement : imageElement;
      pendingFrameIndex = clampedFrameIndex;
      nextElement.loading = 'eager';
      nextElement.fetchPriority = 'high';
      nextElement.src = nextFrameSrc;

      const finishTransition = (ready) => {
        if (cancelled || pendingFrameIndex !== clampedFrameIndex) {
          return;
        }

        pendingFrameIndex = -1;
        const remainingDelta = storyFrameTargetIndexRef.current - storyFrameCurrentIndexRef.current;
        const transitionDelta = clampedFrameIndex - storyFrameCurrentIndexRef.current;
        const stillMovingTowardTarget =
          remainingDelta !== 0 &&
          Math.sign(transitionDelta) === Math.sign(remainingDelta) &&
          Math.abs(transitionDelta) <= Math.abs(remainingDelta);

        if (ready && nextElement.naturalWidth > 0 && stillMovingTowardTarget) {
          nextElement.style.visibility = 'visible';
          nextElement.removeAttribute('aria-hidden');
          visibleElement.style.visibility = 'hidden';
          visibleElement.setAttribute('aria-hidden', 'true');
          nextElement.dataset.frameIndex = String(clampedFrameIndex);
          storyFrameVisibleSlotRef.current = storyFrameVisibleSlotRef.current === 0 ? 1 : 0;
          storyFrameCurrentIndexRef.current = clampedFrameIndex;
        } else if (!ready) {
          storyLoadedFrameIndexesRef.current.delete(clampedFrameIndex);
        }

        requestAnimationTick();
      };

      if (typeof nextElement.decode === 'function') {
        nextElement.decode().then(() => finishTransition(true)).catch(() => finishTransition(false));
      } else if (nextElement.complete && nextElement.naturalWidth > 0) {
        finishTransition(true);
      } else {
        nextElement.onload = () => finishTransition(true);
        nextElement.onerror = () => finishTransition(false);
      }
    };

    if (imageElement.complete && imageElement.naturalWidth > 0) {
      storyLoadedFrameIndexesRef.current.add(0);
    }

    const requestAnimationTick = () => {
      if (storyFrameAnimationFrameRef.current) {
        return;
      }

      storyFrameAnimationFrameRef.current = window.requestAnimationFrame(animateFrames);
    };

    const queueFrameLoad = (frameIndex, priority = 'auto') => {
      if (
        cancelled ||
        frameIndex < 0 ||
        frameIndex > lastFrameIndex ||
        storyLoadedFrameIndexesRef.current.has(frameIndex) ||
        storyPendingFrameIndexesRef.current.has(frameIndex) ||
        storyPendingFrameIndexesRef.current.size >= loadingProfile.maxPendingFrameCount
      ) {
        return;
      }

      const frameSrc = storyFrameSources[frameIndex];

      if (!frameSrc) {
        return;
      }

      const frameImage = new Image();
      let settled = false;
      const finalizeLoad = () => {
        if (cancelled || settled) {
          return;
        }

        settled = true;
        storyPendingFrameIndexesRef.current.delete(frameIndex);
        storyFrameCacheRef.current.delete(frameIndex);
        storyLoadedFrameIndexesRef.current.add(frameIndex);

        if (
          Math.abs(storyFrameTargetIndexRef.current - frameIndex) <= STORY_FRAME_PRELOAD_RESUME_WINDOW ||
          Math.abs(storyFrameCurrentIndexRef.current - frameIndex) <= STORY_FRAME_PRELOAD_RESUME_WINDOW
        ) {
          requestAnimationTick();
        }
      };
      const finalizeError = () => {
        if (cancelled || settled) {
          return;
        }

        settled = true;
        storyPendingFrameIndexesRef.current.delete(frameIndex);
        storyFrameCacheRef.current.delete(frameIndex);
      };

      frameImage.decoding = 'async';

      if ('fetchPriority' in frameImage && priority === 'high') {
        frameImage.fetchPriority = 'high';
      }

      storyPendingFrameIndexesRef.current.add(frameIndex);
      storyFrameCacheRef.current.set(frameIndex, frameImage);

      frameImage.onload = finalizeLoad;
      frameImage.onerror = finalizeError;
      frameImage.src = frameSrc;

      if (frameImage.complete && frameImage.naturalWidth > 0) {
        finalizeLoad();
      }
    };

    const preloadFrameWindow = (centerFrameIndex, direction = 0) => {
      queueFrameLoad(centerFrameIndex, 'high');

      for (let offset = 1; offset <= loadingProfile.lookaheadFrameCount; offset += 1) {
        const forwardIndex = centerFrameIndex + offset;
        const backwardIndex = centerFrameIndex - offset;

        if (direction >= 0) {
          queueFrameLoad(forwardIndex, offset <= STORY_FRAME_PRELOAD_RESUME_WINDOW ? 'high' : 'auto');
        }

        if (direction <= 0 || offset <= loadingProfile.lookbackFrameCount) {
          queueFrameLoad(
            backwardIndex,
            offset <= loadingProfile.lookbackFrameCount ? 'high' : 'auto',
          );
        }

        if (direction < 0) {
          queueFrameLoad(backwardIndex, offset <= STORY_FRAME_PRELOAD_RESUME_WINDOW ? 'high' : 'auto');
        }

        if (direction > 0 && offset <= loadingProfile.lookbackFrameCount) {
          queueFrameLoad(forwardIndex, 'high');
        }
      }
    };

    const findRenderableFrameIndex = (targetFrameIndex, direction) => {
      if (storyLoadedFrameIndexesRef.current.has(targetFrameIndex)) {
        return targetFrameIndex;
      }

      const preferredDirection = direction === 0 ? 1 : direction;
      const searchLimit = Math.max(
        loadingProfile.lookaheadFrameCount,
        loadingProfile.lookbackFrameCount,
      );

      for (let offset = 1; offset <= searchLimit; offset += 1) {
        const preferredIndex = clamp(targetFrameIndex - preferredDirection * offset, 0, lastFrameIndex);

        if (storyLoadedFrameIndexesRef.current.has(preferredIndex)) {
          return preferredIndex;
        }

        const oppositeIndex = clamp(targetFrameIndex + preferredDirection * offset, 0, lastFrameIndex);

        if (storyLoadedFrameIndexesRef.current.has(oppositeIndex)) {
          return oppositeIndex;
        }
      }

      return storyFrameCurrentIndexRef.current;
    };

    const animateFrames = () => {
      storyFrameAnimationFrameRef.current = 0;

      if (pendingFrameIndex !== -1) {
        return;
      }

      const currentFrameIndex = storyFrameCurrentIndexRef.current;
      const targetFrameIndex = storyFrameTargetIndexRef.current;
      const frameDelta = targetFrameIndex - currentFrameIndex;

      if (frameDelta === 0) {
        return;
      }

      if (Math.abs(frameDelta) > loadingProfile.maxSmoothFrameLag) {
        const catchUpIndex = findRenderableFrameIndex(targetFrameIndex, Math.sign(frameDelta));
        if (Math.abs(catchUpIndex - currentFrameIndex) > loadingProfile.maxSmoothFrameLag) {
          renderFrame(catchUpIndex);
          return;
        }
      }

      const frameStep =
        Math.sign(frameDelta) *
        Math.min(
          STORY_FRAME_STEP_MAX,
          Math.max(1, Math.ceil(Math.abs(frameDelta) / STORY_FRAME_ANIMATION_DIVISOR)),
        );
      const direction = Math.sign(frameDelta);
      const nextFrameIndex = clamp(currentFrameIndex + frameStep, 0, lastFrameIndex);
      const nextRenderableFrameIndex = findRenderableFrameIndex(nextFrameIndex, direction);

      preloadFrameWindow(nextFrameIndex, direction);

      if (
        Math.sign(nextRenderableFrameIndex - currentFrameIndex) !== direction ||
        Math.abs(nextRenderableFrameIndex - currentFrameIndex) > Math.abs(frameDelta)
      ) {
        return;
      }

      if (nextRenderableFrameIndex !== storyFrameCurrentIndexRef.current) {
        renderFrame(nextRenderableFrameIndex);
      }

      if (
        pendingFrameIndex === -1 &&
        storyFrameCurrentIndexRef.current !== storyFrameTargetIndexRef.current &&
        nextRenderableFrameIndex !== currentFrameIndex
      ) {
        storyFrameAnimationFrameRef.current = window.requestAnimationFrame(animateFrames);
      }
    };

    const stopContinuousSync = () => {
      if (!storyFrameSyncFrameRef.current) {
        return;
      }

      window.cancelAnimationFrame(storyFrameSyncFrameRef.current);
      storyFrameSyncFrameRef.current = 0;
    };

    const syncTargetToScroll = () => {
      const sectionBounds = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const animationScrollDistance = viewportHeight * STORY_SCROLL_DISTANCE_VIEWPORTS;
      const progress = clamp((viewportHeight - sectionBounds.top) / animationScrollDistance);
      const nextTargetFrameIndex = Math.round(lastFrameIndex * progress);
      const direction = Math.sign(nextTargetFrameIndex - storyFrameCurrentIndexRef.current);

      storyFrameTargetIndexRef.current = nextTargetFrameIndex;
      if (Number.isFinite(loadingProfile.maxPendingFrameCount)) {
        for (const [frameIndex, frameImage] of storyFrameCacheRef.current) {
          if (
            Math.abs(frameIndex - nextTargetFrameIndex) > loadingProfile.lookaheadFrameCount + loadingProfile.lookbackFrameCount &&
            Math.abs(frameIndex - storyFrameCurrentIndexRef.current) > loadingProfile.lookaheadFrameCount + loadingProfile.lookbackFrameCount
          ) {
            frameImage.onload = null;
            frameImage.onerror = null;
            frameImage.src = '';
            storyFrameCacheRef.current.delete(frameIndex);
            storyPendingFrameIndexesRef.current.delete(frameIndex);
          }
        }
      }
      preloadFrameWindow(nextTargetFrameIndex, direction);
      requestAnimationTick();
    };

    const continueSyncWhileInteracting = (timestamp) => {
      storyFrameSyncFrameRef.current = 0;
      syncTargetToScroll();

      const scrollPosition = getScrollPosition();
      const shouldKeepSyncing =
        !document.hidden &&
        (timestamp < storyTouchTrackingUntilRef.current ||
          Math.abs(scrollPosition - storyLastTrackedScrollYRef.current) > STORY_POINTER_SCROLL_DELTA);

      storyLastTrackedScrollYRef.current = scrollPosition;

      if (shouldKeepSyncing) {
        storyFrameSyncFrameRef.current =
          window.requestAnimationFrame(continueSyncWhileInteracting);
      }
    };

    const extendContinuousSync = (durationMs = STORY_INTERACTION_SYNC_MS) => {
      storyTouchTrackingUntilRef.current = Math.max(
        storyTouchTrackingUntilRef.current,
        performance.now() + durationMs,
      );

      if (storyFrameSyncFrameRef.current) {
        return;
      }

      storyLastTrackedScrollYRef.current = getScrollPosition();
      storyFrameSyncFrameRef.current = window.requestAnimationFrame(continueSyncWhileInteracting);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopContinuousSync();

        if (storyFrameAnimationFrameRef.current) {
          window.cancelAnimationFrame(storyFrameAnimationFrameRef.current);
          storyFrameAnimationFrameRef.current = 0;
        }

        return;
      }

      syncTargetToScroll();
    };

    const handleScroll = () => {
      syncTargetToScroll();
      extendContinuousSync(STORY_INTERACTION_MOMENTUM_MS);
    };

    const handleTouchInteractionStart = () => {
      syncTargetToScroll();
      extendContinuousSync(STORY_INTERACTION_SYNC_MS);
    };

    const handleTouchInteractionMove = () => {
      syncTargetToScroll();
      extendContinuousSync(STORY_INTERACTION_SYNC_MS);
    };

    const handleTouchInteractionEnd = () => {
      extendContinuousSync(STORY_INTERACTION_MOMENTUM_MS);
    };

    const handlePointerMove = (event) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
        return;
      }

      handleTouchInteractionMove();
    };

    const handlePointerUp = (event) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
        return;
      }

      handleTouchInteractionEnd();
    };

    const handleWheel = () => {
      syncTargetToScroll();
      extendContinuousSync(STORY_INTERACTION_SYNC_MS);
    };

    renderFrame(0);
    syncTargetToScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', syncTargetToScroll);
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchstart', handleTouchInteractionStart, { passive: true });
    window.addEventListener('touchmove', handleTouchInteractionMove, { passive: true });
    window.addEventListener('touchend', handleTouchInteractionEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchInteractionEnd, { passive: true });
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerup', handlePointerUp, { passive: true });
    window.addEventListener('pointercancel', handlePointerUp, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopContinuousSync();

      if (storyFrameAnimationFrameRef.current) {
        window.cancelAnimationFrame(storyFrameAnimationFrameRef.current);
        storyFrameAnimationFrameRef.current = 0;
      }

      bufferElement.onload = null;
      bufferElement.onerror = null;
      imageElement.onload = null;
      imageElement.onerror = null;

      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', syncTargetToScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchInteractionStart);
      window.removeEventListener('touchmove', handleTouchInteractionMove);
      window.removeEventListener('touchend', handleTouchInteractionEnd);
      window.removeEventListener('touchcancel', handleTouchInteractionEnd);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [storyFrameSources, storyLoadingEnabled]);

  return (
    <section className="home">
      <div className="home-hero-shell" ref={heroRef}>
        <picture className="home-media">
          <source
            srcSet={HOME_HERO_DESKTOP_IMAGE}
            media="(min-width: 768px)"
          />
          <source
            srcSet={HOME_HERO_MOBILE_IMAGE}
            media="(max-width: 767px)"
          />
          <img
            src={HOME_HERO_MOBILE_IMAGE}
            alt={branding?.displayName || 'Glace Confeitaria'}
            className="home-image"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </picture>

        <div className="home-hero-content">
          <div className="home-hero-copy-column">
            <div className="home-carousel-copy">
              {(hero?.eyebrow || branding?.tagline) && (
                <p className="home-carousel-kicker">{hero?.eyebrow || branding?.tagline}</p>
              )}
              <h1 className="home-carousel-phrase">{heroPhrase}</h1>
            </div>

            <div className="home-cta-wrap">
              <PublicActionLink link={heroCta} className="btn btn-primary home-cta-button" />
            </div>
          </div>

          <div className="home-carousel-column">
            <HomeCarousel
              slides={slides}
              ariaLabel={hero?.headline || 'Carrossel de fotos'}
            />
          </div>
        </div>

        <button type="button" className="home-story-teaser" onClick={scrollToStory}>
          <span className="home-story-teaser-arrow" aria-hidden="true">
            ↓
          </span>
          <span className="home-story-teaser-text">{storyPrompt}</span>
        </button>
      </div>

      <section className="home-story-shell" ref={storySectionRef} aria-labelledby="home-story-title">
        <div className="home-story-stage">
          <article className="home-story-card">
            <div className="home-story-video-frame">
              <img
                ref={storyFrameImageRef}
                className="home-story-video"
                src={storyPosterSrc}
                alt={'Sequ\u00eancia animada do doce Glac\u00ea'}
                loading="lazy"
                fetchPriority="low"
                decoding="async"
                draggable={false}
              />
              <img
                ref={storyFrameBufferRef}
                className="home-story-video home-story-video--buffer"
                alt={'Sequ\u00eancia animada do doce Glac\u00ea'}
                aria-hidden="true"
                decoding="async"
                draggable={false}
              />
            </div>

            <div className="home-story-copy">
              <p className="home-story-kicker">{storyEyebrow}</p>
              <h2 className="home-story-title" id="home-story-title">
                {storyTitle}
              </h2>
              <p className="home-story-text">{storyText}</p>
            </div>
          </article>
        </div>
      </section>

      {instagramHref ? (
        <section className="home-instagram-shell" aria-labelledby="home-instagram-title">
          <a
            href={instagramHref}
            className="home-instagram-card"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Seguir a Glacê no Instagram"
          >
            <div className="home-instagram-icon-wrap" aria-hidden="true">
              <FaInstagram className="home-instagram-icon" />
            </div>

            <div className="home-instagram-copy">
              <p className="home-instagram-kicker">Instagram</p>
              <h2 className="home-instagram-title" id="home-instagram-title">
                Siga a Glacê e acompanhe nossas novidades
              </h2>
              <p className="home-instagram-text">
                Veja bastidores, lançamentos, presentes especiais e tudo o que sai da nossa
                cozinha com carinho.
              </p>
            </div>

            <span className="home-instagram-cta">
              {instagramHandle ? `Seguir ${instagramHandle}` : 'Seguir no Instagram'}
            </span>
          </a>
        </section>
      ) : null}
    </section>
  );
}

export default Home;
